'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search, X, Calendar, Clock, ArrowRight, Loader2 } from 'lucide-react';
import type { BlogPost } from '@/lib/data/blog-d1';
import { formatDate } from '@/lib/utils/format-date';

interface BlogSearchClientProps {
  totalPosts: number;
  children: React.ReactNode;
}

interface SearchResult {
  query: string;
  count: number;
  posts: BlogPost[];
}

export default function BlogSearchClient({ totalPosts, children }: BlogSearchClientProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BlogPost[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resultCount, setResultCount] = useState<number | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Perform search when debounced query changes
  const performSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults(null);
      setResultCount(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/api/blog/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data: SearchResult = await response.json();
        setSearchResults(data.posts);
        setResultCount(data.count);
      } else {
        setSearchResults([]);
        setResultCount(0);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setResultCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    performSearch(debouncedQuery);
  }, [debouncedQuery, performSearch]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setResultCount(null);
  };

  const isSearching = searchQuery.trim().length > 0;

  return (
    <>
      {/* Search Bar Section */}
      <section className="py-8 px-4 bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles..."
              aria-label="Search articles"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-4 pl-12 rounded-xl border-2 border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2813e8] focus:ring-2 focus:ring-[#2813e8]/20 transition-all shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {isSearching && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : resultCount !== null ? (
                <span>
                  {resultCount} {resultCount === 1 ? 'post' : 'posts'} found for &quot;{searchQuery}&quot;
                </span>
              ) : searchQuery.length < 2 ? (
                <span>Type at least 2 characters to search</span>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {/* Search Results - shown when searching and results are fetched */}
      {isSearching && searchResults !== null ? (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            {searchResults.length > 0 ? (
              <>
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] bg-clip-text text-transparent">
                    Search Results
                  </h2>
                  <p className="text-lg text-gray-600">
                    Found {resultCount} {resultCount === 1 ? 'article' : 'articles'} matching &quot;{searchQuery}&quot;
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                  {searchResults.map((post) => (
                    <Link
                      key={post.slug}
                      href={`/blog/${post.slug}`}
                      className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col"
                    >
                      {/* Featured Image */}
                      {post.featuredImage ? (
                        <div className="relative h-52 w-full bg-gray-100 overflow-hidden">
                          <Image
                            src={post.featuredImage}
                            alt={post.title}
                            fill
                            className="object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      ) : (
                        <div className="h-52 w-full bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] flex items-center justify-center">
                          <svg className="w-16 h-16 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        {/* Category Badges */}
                        {post.categories && post.categories.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.categories.slice(0, 2).map((category, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-50 to-cyan-50 text-[#2813e8] border border-blue-100"
                              >
                                {category}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Title */}
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2813e8] transition-colors line-clamp-2 leading-tight">
                          {post.title}
                        </h3>

                        {/* Excerpt */}
                        {post.excerpt && (
                          <p className="text-gray-600 mb-4 line-clamp-3 flex-grow text-sm leading-relaxed">
                            {post.excerpt}
                          </p>
                        )}

                        {/* Meta Info */}
                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            {post.date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(post.date, { format: 'short' })}
                              </span>
                            )}
                            {post.readTime && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {post.readTime} min
                              </span>
                            )}
                          </div>
                          <span
                            className="inline-flex items-center gap-1 text-[#2813e8] font-semibold group-hover:gap-2 transition-all text-sm"
                          >
                            Read
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="text-center">
                  <button
                    onClick={clearSearch}
                    className="inline-flex items-center gap-2 text-[#2813e8] font-semibold hover:underline"
                  >
                    <X className="w-4 h-4" />
                    Clear search and browse all {totalPosts} articles
                  </button>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="text-5xl mb-4">🔍</div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">No articles found</h3>
                <p className="text-gray-500 mb-4">
                  No results for &quot;{searchQuery}&quot;. Try different keywords.
                </p>
                <button
                  onClick={clearSearch}
                  className="text-[#2813e8] font-semibold hover:underline"
                >
                  Clear search and browse all articles
                </button>
              </div>
            )}
          </div>
        </section>
      ) : (
        /* Normal Browse Mode - show children (category filter, series carousel, paginated posts) */
        children
      )}
    </>
  );
}
